import { Fragment, useEffect, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, Check, AlertTriangle, X, Landmark, Loader2, Info, ExternalLink, Bookmark, Gift, ArrowRight } from "lucide-react";
import { Seo } from "@/lib/seo";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL;
const LENDER_MATCH_URL = "https://www.sba.gov/funding-programs/loans/lender-match";

// Render lightweight markdown **bold** as real <strong> (AI text sometimes uses asterisks).
const renderMd = (text) => String(text || "").split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
  part.startsWith("**") && part.endsWith("**")
    ? <strong key={i} className="text-white">{part.slice(2, -2)}</strong>
    : <Fragment key={i}>{part}</Fragment>
);

const ICON = {
  ready: <Check size={16} className="text-emerald-400" />,
  attention: <AlertTriangle size={16} className="text-amber-400" />,
  missing: <X size={16} className="text-red-400" />,
};
const LEVEL_COLOR = {
  "Lender Ready": "text-emerald-400 border-emerald-400/40 bg-emerald-500/10",
  "Nearly Ready": "text-gold border-gold/40 bg-gold/10",
  "Developing": "text-amber-400 border-amber-400/40 bg-amber-500/10",
  "Not Ready": "text-red-400 border-red-400/40 bg-red-500/10",
};

const Toggle = ({ label, val, onChange, testid }) => (
  <button type="button" data-testid={testid} onClick={() => onChange(!val)}
    className={`flex items-center gap-2 px-3 py-2 border text-sm text-left transition-colors ${val ? "border-gold bg-gold/10 text-white" : "border-white/15 text-gray-300 hover:border-white/30"}`}>
    <span className={`w-4 h-4 border flex items-center justify-center shrink-0 ${val ? "bg-gold border-gold" : "border-white/30"}`}>{val && <Check size={11} className="text-navy" />}</span>
    {label}
  </button>
);

export default function FundingReadiness() {
  const { user } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [f, setF] = useState({
    operating: true, for_profit: true, us_based: true, months_operating: 12,
    annual_revenue: 0, keeps_records: false, credit_band: "good", collateral_available: false,
    owner_contribution: false, ar_inventory: false, docs_ready: [], ineligible_type: false,
    small_by_sba: true, credit_elsewhere: true, use_of_funds: [],
    cash_available_for_debt_service: 0, loan_amount: 0, interest_rate_pct: 11.5, term_months: 120,
  });
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const [exp, setExp] = useState(null);
  const [expBusy, setExpBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [planGrants, setPlanGrants] = useState(null); // {has_plan, grants} for signed-in clients

  useEffect(() => { axios.get(`${API}/api/funding-readiness/config`).then(r => setCfg(r.data)); }, []);
  useEffect(() => {
    if (user) api.get("/funding-readiness/my-grants").then(r => setPlanGrants(r.data)).catch(() => setPlanGrants(null));
  }, [user]);

  const set = (k, v) => setF(p => ({ ...p, [k]: v }));
  const toggleArr = (k, v) => setF(p => ({ ...p, [k]: p[k].includes(v) ? p[k].filter(x => x !== v) : [...p[k], v] }));

  const evaluate = async () => {
    setBusy(true); setExp(null); setErr(null); setSaved(false);
    try { const r = await axios.post(`${API}/api/funding-readiness/evaluate`, f); setRes(r.data); }
    catch { setErr("We couldn't run your readiness check just now. Please try again in a moment."); }
    finally { setBusy(false); }
    setTimeout(() => document.getElementById("fr-result")?.scrollIntoView({ behavior: "smooth" }), 100);
  };

  const explain = async () => {
    setExpBusy(true); setErr(null);
    try { const r = await axios.post(`${API}/api/funding-readiness/explain`, f); setExp(r.data); }
    catch { setErr("Top Foster couldn't generate the plain-English explanation just now. Please try again."); }
    finally { setExpBusy(false); }
  };

  const saveCheck = async () => {
    setSaving(true);
    try { await api.post("/funding-readiness/save", f); setSaved(true); toast.success("Saved to your portal — track it under Funding Readiness."); }
    catch { toast.error("Couldn't save your check. Please try again."); }
    finally { setSaving(false); }
  };

  const downloadPacket = () => {
    if (!res) return;
    const cat = res.categories.map(c => `<tr><td>${c.area}</td><td>${c.status.toUpperCase()}</td><td>${c.note}</td></tr>`).join("");
    const scen = (res.scenarios || []).map(s => `<tr><td>${s.label}</td><td>$${s.loan_amount.toLocaleString()}</td><td>$${s.annual_debt_service.toLocaleString()}</td><td>${s.dscr}x</td></tr>`).join("");
    const gaps = res.gaps.map(g => `<li>${g}</li>`).join("");
    const html = `<html><head><title>Funding Readiness Packet — ATF WebWorks</title>
      <style>body{font-family:Georgia,serif;color:#111;max-width:760px;margin:40px auto;padding:0 24px;line-height:1.5}
      h1{font-size:24px} h2{font-size:16px;border-bottom:2px solid #C9A227;padding-bottom:4px;margin-top:28px}
      table{width:100%;border-collapse:collapse;font-size:13px;margin-top:8px} td,th{border:1px solid #ccc;padding:6px;text-align:left}
      .lvl{display:inline-block;border:2px solid #C9A227;padding:6px 14px;font-weight:bold;margin:8px 0}
      .disc{font-size:11px;color:#666;margin-top:24px;border-top:1px solid #ccc;padding-top:10px}</style></head><body>
      <h1>Funding Readiness Packet</h1><div>Prepared by ATF WebWorks — Lender preparation summary</div>
      <div class="lvl">Funding Readiness: ${res.readiness_level}</div>
      <h2>Use of Funds</h2><div>${(res.use_of_funds||[]).join(", ") || "Not specified"}</div>
      <h2>Debt-Service Coverage (estimate)</h2>
      <div>Cash available: $${res.dscr.cash_available.toLocaleString()} · Est. annual payments: $${res.dscr.annual_debt_service.toLocaleString()} · DSCR: <b>${res.dscr.value ?? "—"}x</b></div>
      <div>${res.dscr.reading}</div>
      ${scen ? `<h2>Loan Scenarios</h2><table><tr><th>Scenario</th><th>Loan</th><th>Annual Debt</th><th>DSCR</th></tr>${scen}</table>` : ""}
      <h2>Readiness by Category</h2><table><tr><th>Area</th><th>Status</th><th>Note</th></tr>${cat}</table>
      <h2>SBA 7(a) Eligibility (self-attested)</h2><div>${res.sba_7a_eligibility.status}</div>
      ${exp?.summary ? `<h2>Summary</h2><div>${exp.summary.replace(/\*\*/g,"")}</div>` : ""}
      ${exp?.ai_expense_narrative ? `<h2>Use-of-Funds Narrative</h2><div>${exp.ai_expense_narrative}</div>` : ""}
      <h2>To Do Before Applying</h2><ul>${gaps || "<li>None identified.</li>"}</ul>
      <div class="disc">${res.disclaimer}</div>
      <script>window.onload=()=>window.print()</script></body></html>`;
    const w = window.open("", "_blank");
    if (w) { w.document.write(html); w.document.close(); }
  };

  if (!cfg) return <div className="min-h-screen bg-navy flex items-center justify-center"><Loader2 className="animate-spin text-gold" /></div>;

  return (
    <div className="min-h-screen bg-navy pt-28 pb-24" data-testid="funding-readiness-page">
      <Seo title="Funding Readiness — Are You Lender-Ready? | ATF WebWorks" description="A free, honest readiness check for SBA 7(a) and other financing. See how prepared you appear for a lender conversation — and exactly what to fix first." path="/funding-readiness" />
      <div className="max-w-4xl mx-auto px-6">
        <div className="flex items-center gap-2 text-gold text-xs uppercase tracking-[0.25em] mb-4"><Landmark size={15} /> Funding Readiness</div>
        <h1 className="font-heading text-4xl sm:text-5xl text-white tracking-tight">Are you lender-ready?</h1>
        <p className="text-gray-400 mt-4 max-w-2xl">Answer a few questions and we'll show — honestly — how prepared you appear for a lender conversation, plus exactly what to fix first. We start with SBA 7(a) rules; nothing here is a loan decision.</p>

        <div className="mt-10 space-y-8">
          {/* Business condition */}
          <section className="border border-white/10 bg-navy-800 p-6">
            <h2 className="font-heading text-lg text-white mb-4">Your business</h2>
            <div className="grid sm:grid-cols-2 gap-3 mb-4">
              <label className="text-sm text-gray-300">Months operating
                <input type="number" data-testid="fr-months" value={f.months_operating} onChange={e => set("months_operating", +e.target.value)} className="mt-1 w-full bg-navy border border-white/15 px-3 py-2 text-white" /></label>
              <label className="text-sm text-gray-300">Annual revenue ($)
                <input type="number" data-testid="fr-revenue" value={f.annual_revenue} onChange={e => set("annual_revenue", +e.target.value)} className="mt-1 w-full bg-navy border border-white/15 px-3 py-2 text-white" /></label>
              <label className="text-sm text-gray-300">Credit (self-reported)
                <select data-testid="fr-credit" value={f.credit_band} onChange={e => set("credit_band", e.target.value)} className="mt-1 w-full bg-navy border border-white/15 px-3 py-2 text-white capitalize">
                  {cfg.credit_bands.map(b => <option key={b} value={b}>{b}</option>)}</select></label>
            </div>
            <div className="flex flex-wrap gap-2">
              <Toggle label="Operating business" val={f.operating} onChange={v => set("operating", v)} testid="fr-operating" />
              <Toggle label="For-profit" val={f.for_profit} onChange={v => set("for_profit", v)} testid="fr-forprofit" />
              <Toggle label="U.S.-based" val={f.us_based} onChange={v => set("us_based", v)} testid="fr-usbased" />
              <Toggle label="Keeps financial records" val={f.keeps_records} onChange={v => set("keeps_records", v)} testid="fr-records" />
              <Toggle label="Collateral available" val={f.collateral_available} onChange={v => set("collateral_available", v)} testid="fr-collateral" />
              <Toggle label="Owner contribution" val={f.owner_contribution} onChange={v => set("owner_contribution", v)} testid="fr-owner" />
            </div>
          </section>

          {/* Use of funds */}
          <section className="border border-white/10 bg-navy-800 p-6">
            <h2 className="font-heading text-lg text-white mb-1">What is the money for?</h2>
            <p className="text-gray-500 text-sm mb-4">Pick all that apply.</p>
            <div className="flex flex-wrap gap-2">
              {cfg.use_of_funds.map(u => (
                <Toggle key={u} label={u} val={f.use_of_funds.includes(u)} onChange={() => toggleArr("use_of_funds", u)} testid={`fr-uof-${u.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} />
              ))}
            </div>
          </section>

          {/* Documents you have ready */}
          <section className="border border-white/10 bg-navy-800 p-6">
            <h2 className="font-heading text-lg text-white mb-1">Which documents do you have ready?</h2>
            <p className="text-gray-500 text-sm mb-4">Lenders expect these. Check the ones you already have.</p>
            <div className="flex flex-wrap gap-2">
              {cfg.required_docs.map(d => (
                <Toggle key={d} label={d} val={f.docs_ready.includes(d)} onChange={() => toggleArr("docs_ready", d)} testid={`fr-doc-${d.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} />
              ))}
            </div>
          </section>

          {/* SBA 7(a) basics — self-attested */}
          <section className="border border-white/10 bg-navy-800 p-6">
            <h2 className="font-heading text-lg text-white mb-1">SBA 7(a) basics</h2>
            <p className="text-gray-500 text-sm mb-4">A few self-attested questions. A lender confirms these — this is not a decision.</p>
            <div className="grid sm:grid-cols-2 gap-3 mb-4">
              <label className="text-sm text-gray-300">Small business under SBA size standards?
                <select data-testid="fr-small" value={f.small_by_sba === null ? "unsure" : f.small_by_sba ? "yes" : "no"}
                  onChange={e => set("small_by_sba", e.target.value === "unsure" ? null : e.target.value === "yes")}
                  className="mt-1 w-full bg-navy border border-white/15 px-3 py-2 text-white">
                  <option value="yes">Yes</option><option value="no">No</option><option value="unsure">Not sure</option>
                </select></label>
            </div>
            <div className="flex flex-wrap gap-2">
              <Toggle label="Can't get reasonable credit elsewhere on your own" val={f.credit_elsewhere} onChange={v => set("credit_elsewhere", v)} testid="fr-credit-elsewhere" />
              <Toggle label="SBA-ineligible business type (lending, gambling, speculation)" val={f.ineligible_type} onChange={v => set("ineligible_type", v)} testid="fr-ineligible" />
            </div>
          </section>

          {/* DSCR */}
          <section className="border border-white/10 bg-navy-800 p-6">
            <h2 className="font-heading text-lg text-white mb-1">Can the business support the loan?</h2>
            <p className="text-gray-500 text-sm mb-4">We estimate Debt-Service Coverage from your numbers.</p>
            <div className="grid sm:grid-cols-2 gap-3">
              <label className="text-sm text-gray-300">Loan amount ($)
                <input type="number" data-testid="fr-loan" value={f.loan_amount} onChange={e => set("loan_amount", +e.target.value)} className="mt-1 w-full bg-navy border border-white/15 px-3 py-2 text-white" /></label>
              <label className="text-sm text-gray-300">Annual cash available for debt service ($)
                <input type="number" data-testid="fr-cash" value={f.cash_available_for_debt_service} onChange={e => set("cash_available_for_debt_service", +e.target.value)} className="mt-1 w-full bg-navy border border-white/15 px-3 py-2 text-white" /></label>
              <label className="text-sm text-gray-300">Interest rate (%)
                <input type="number" step="0.1" data-testid="fr-rate" value={f.interest_rate_pct} onChange={e => set("interest_rate_pct", +e.target.value)} className="mt-1 w-full bg-navy border border-white/15 px-3 py-2 text-white" /></label>
              <label className="text-sm text-gray-300">Term (months)
                <input type="number" data-testid="fr-term" value={f.term_months} onChange={e => set("term_months", +e.target.value)} className="mt-1 w-full bg-navy border border-white/15 px-3 py-2 text-white" /></label>
            </div>
          </section>

          <button onClick={evaluate} disabled={busy} data-testid="fr-evaluate-btn"
            className="w-full bg-gold text-navy py-4 font-semibold hover:bg-gold-light transition-colors flex items-center justify-center gap-2">
            {busy ? <Loader2 className="animate-spin" size={18} /> : <ShieldCheck size={18} />} Check my readiness
          </button>
          {err && <p className="text-sm text-red-400 flex items-center gap-2" data-testid="fr-error"><AlertTriangle size={15} /> {err}</p>}
        </div>

        {res && (
          <motion.div id="fr-result" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mt-12" data-testid="fr-result">
            <div className={`inline-flex items-center gap-2 px-4 py-2 border font-heading text-lg ${LEVEL_COLOR[res.readiness_level]}`} data-testid="fr-level">
              <ShieldCheck size={18} /> Funding Readiness: {res.readiness_level}
            </div>

            <div className="mt-6 border border-white/10 divide-y divide-white/10" data-testid="fr-categories">
              {res.categories.map(c => (
                <div key={c.area} className="flex items-start gap-3 px-5 py-3">
                  <span className="mt-0.5 shrink-0">{ICON[c.status]}</span>
                  <div><div className="text-white text-sm font-medium">{c.area}</div><div className="text-gray-400 text-xs">{c.note}</div></div>
                </div>
              ))}
            </div>

            {/* DSCR */}
            <div className="mt-6 border border-gold/25 bg-gold/5 p-5" data-testid="fr-dscr">
              <div className="text-gold text-xs uppercase tracking-[0.2em] mb-2">Estimated Debt-Service Coverage</div>
              {res.dscr.value != null ? (
                <div className="flex flex-wrap gap-x-8 gap-y-1 text-sm text-gray-300">
                  <span>Cash available: <b className="text-white">${res.dscr.cash_available.toLocaleString()}</b></span>
                  <span>Est. annual payments: <b className="text-white">${res.dscr.annual_debt_service.toLocaleString()}</b></span>
                  <span>DSCR: <b className="text-gold">{res.dscr.value.toFixed(2)}×</b></span>
                </div>
              ) : null}
              <p className="text-gray-400 text-xs mt-2">{res.dscr.reading}</p>
            </div>

            {/* Loan scenarios */}
            {res.scenarios && res.scenarios.length > 0 && (
              <div className="mt-6 border border-white/10 p-5" data-testid="fr-scenarios">
                <div className="text-white text-sm font-medium mb-3">How much debt could you support?</div>
                <div className="grid grid-cols-4 gap-2 text-xs">
                  <div className="text-gray-500">Scenario</div><div className="text-gray-500">Loan</div><div className="text-gray-500">Annual debt</div><div className="text-gray-500">DSCR</div>
                  {res.scenarios.map(s => (
                    <Fragment key={s.label}>
                      <div className="text-gray-300">{s.label}</div>
                      <div className="text-white">${s.loan_amount.toLocaleString()}</div>
                      <div className="text-gray-300">${s.annual_debt_service.toLocaleString()}</div>
                      <div className={s.dscr >= 1.25 ? "text-emerald-400" : s.dscr >= 1 ? "text-amber-400" : "text-red-400"}>{s.dscr}x</div>
                    </Fragment>
                  ))}
                </div>
              </div>
            )}

            {/* AI explanation */}
            <div className="mt-6" data-testid="fr-explain-section">
              {!exp ? (
                <button onClick={explain} disabled={expBusy} data-testid="fr-explain-btn"
                  className="border border-gold/40 text-gold px-5 py-2.5 text-sm hover:bg-gold/10 transition-colors flex items-center gap-2">
                  {expBusy ? <Loader2 className="animate-spin" size={15} /> : <Info size={15} />} Explain this in plain English
                </button>
              ) : (
                <div className="border border-white/10 bg-navy-800 p-5 space-y-3">
                  <div className="text-gold text-xs uppercase tracking-[0.2em]">Top Foster explains</div>
                  <p className="text-gray-200 text-sm whitespace-pre-line" data-testid="fr-summary">{renderMd(exp.summary)}</p>
                  {exp.ai_expense_narrative && (
                    <div><div className="text-white text-sm font-medium mt-2 mb-1">Use-of-funds narrative</div>
                      <p className="text-gray-300 text-sm whitespace-pre-line" data-testid="fr-narrative">{renderMd(exp.ai_expense_narrative)}</p></div>
                  )}
                </div>
              )}
            </div>

            {/* SBA eligibility — separate */}
            <div className="mt-6 border border-white/10 p-5" data-testid="fr-sba">
              <div className="text-white text-sm font-medium mb-1">SBA 7(a) Eligibility (self-attested)</div>
              <div className="text-gray-300 text-sm">{res.sba_7a_eligibility.status}</div>
              {res.sba_7a_eligibility.failed.length > 0 && (
                <ul className="mt-2 text-xs text-amber-300 list-disc pl-5">{res.sba_7a_eligibility.failed.map(x => <li key={x}>{x}</li>)}</ul>
              )}
              {res.sba_7a_eligibility.size_unsure && <p className="text-xs text-gray-500 mt-1">Confirm your SBA size standard with a lender.</p>}
            </div>

            {/* Gaps */}
            {res.gaps.length > 0 && (
              <div className="mt-6 border border-white/10 p-5" data-testid="fr-gaps">
                <div className="text-white text-sm font-medium mb-3">Fix these before a lender conversation</div>
                <ul className="space-y-2">{res.gaps.map((g, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-gray-300"><AlertTriangle size={14} className="text-amber-400 shrink-0 mt-0.5" /> {g}</li>
                ))}</ul>
              </div>
            )}

            <p className="mt-6 text-xs text-gray-500 flex items-start gap-2" data-testid="fr-disclaimer"><Info size={13} className="shrink-0 mt-0.5" /> {res.disclaimer}</p>

            {/* Funding paths beyond loans — grant tie-in */}
            <div className="mt-8 border border-white/10 p-5" data-testid="fr-grants">
              <div className="flex items-center gap-2 text-white text-sm font-medium mb-1"><Gift size={15} className="text-gold" /> Funding paths beyond loans</div>
              <p className="text-gray-400 text-xs mb-3">A loan isn't the only option. Grants are money you don't repay — worth exploring alongside financing.</p>
              {user && planGrants?.grants?.length > 0 ? (
                <div className="space-y-2" data-testid="fr-grants-list">
                  <p className="text-xs text-gray-500">Grants you've saved in your business plan:</p>
                  {planGrants.grants.map((g, i) => (
                    <div key={i} className="border border-white/10 bg-navy-800 p-3" data-testid={`fr-grant-${i}`}>
                      <div className="text-sm text-white font-medium">{g.name || "Grant opportunity"}</div>
                      {g.provider && <div className="text-xs text-gray-400">{g.provider}</div>}
                      {g.why_fit && <div className="text-xs text-gray-500 mt-1">{g.why_fit}</div>}
                    </div>
                  ))}
                  <Link to="/portal?view=plan" data-testid="fr-grants-open-plan" className="inline-flex items-center gap-1.5 text-gold text-xs font-semibold mt-1 hover:underline">Open Grant Finder in your plan <ArrowRight size={13} /></Link>
                </div>
              ) : (
                <Link to="/business-plan" data-testid="fr-grants-cta" className="inline-flex items-center gap-2 border border-gold/40 text-gold px-4 py-2.5 text-sm hover:bg-gold/10 transition-colors">
                  <Gift size={15} /> {user ? "Find grants with your business plan" : "Build a business plan to unlock AI-matched grants"} <ArrowRight size={14} />
                </Link>
              )}
            </div>

            {/* Take this to a lender — SBA Lender Match handoff */}
            <div className="mt-6 border border-gold/25 bg-gold/5 p-5" data-testid="fr-lender-handoff">
              <div className="flex items-center gap-2 text-white text-sm font-medium mb-1"><Landmark size={15} className="text-gold" /> Take this to a lender</div>
              <p className="text-gray-300 text-xs mb-4">When you're ready, apply directly with a lender. For SBA loans, SBA's free <strong>Lender Match</strong> connects you with participating lenders — ATF doesn't lend or decide. Download your packet and bring it to the conversation.</p>
              <div className="flex flex-wrap gap-3">
                <button onClick={downloadPacket} data-testid="fr-packet-btn"
                  className="bg-gold text-navy px-6 py-3 font-semibold hover:bg-gold-light transition-colors flex items-center gap-2">
                  <ShieldCheck size={16} /> Download lender-prep packet
                </button>
                <a href={LENDER_MATCH_URL} target="_blank" rel="noopener noreferrer" data-testid="fr-lender-match-btn"
                  className="border border-white/20 text-white px-6 py-3 font-semibold hover:border-gold/50 transition-colors flex items-center gap-2">
                  Go to SBA Lender Match <ExternalLink size={15} />
                </a>
              </div>
            </div>

            {/* Save to portal */}
            <div className="mt-6" data-testid="fr-save-section">
              {user ? (
                saved ? (
                  <p className="text-sm text-emerald-400 flex items-center gap-2" data-testid="fr-saved-msg"><Check size={15} /> Saved to your portal — track it under Funding Readiness as you close gaps.</p>
                ) : (
                  <button onClick={saveCheck} disabled={saving} data-testid="fr-save-btn"
                    className="border border-gold/40 text-gold px-5 py-2.5 text-sm hover:bg-gold/10 transition-colors flex items-center gap-2 disabled:opacity-60">
                    {saving ? <Loader2 className="animate-spin" size={15} /> : <Bookmark size={15} />} Save this check to my portal
                  </button>
                )
              ) : (
                <div className="border border-white/10 bg-navy-800 p-4 flex flex-wrap items-center gap-3" data-testid="fr-save-signin">
                  <Bookmark size={15} className="text-gold" />
                  <span className="text-sm text-gray-300">Sign in to save this check and watch your readiness improve over time.</span>
                  <Link to="/login" className="text-gold text-sm font-semibold hover:underline">Sign in</Link>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}
